import type { OnboardingStepId, SetupResponse } from "@/types/onboarding";

export const ONBOARDING_STEP_ORDER: OnboardingStepId[] = [
  "intro",
  "styling",
  "operation",
  "components",
  "voice",
  "scope",
  "complete",
  "results",
];

export const STEP_DISPLAY_NAMES: Record<OnboardingStepId, string> = {
  intro: "Intro",
  styling: "Visual brand",
  operation: "Operation",
  components: "Components",
  voice: "Brand voice",
  scope: "Select tiles",
  complete: "Complete",
  // Hidden terminal step — filtered out of the stepper, so the label is never shown.
  results: "Results",
};

export function allOnboardingStepsComplete(setupData: SetupResponse): boolean {
  return true;
  // don't count the scope/complete/results steps, they have no setup data and
  // exist for flow control / presentational purposes only
  return ONBOARDING_STEP_ORDER.filter(
    (step) => !["scope", "complete", "results"].includes(step),
  ).every((step) => isStepComplete(step, setupData));
}

export function getFirstIncompleteStep(
  setupData: SetupResponse,
): OnboardingStepId {
  for (const step of ONBOARDING_STEP_ORDER) {
    if (!isStepComplete(step, setupData)) {
      return step;
    }
  }
  // If all steps are complete, return the step after the intro
  return ONBOARDING_STEP_ORDER[1];
}

export function getNextStep(
  currentStep: OnboardingStepId,
): OnboardingStepId | "dragon-tiles" {
  const currentIndex = ONBOARDING_STEP_ORDER.indexOf(currentStep);

  if (currentIndex === -1) {
    // If current step is not found, default to styling
    return "styling";
  }

  const nextIndex = currentIndex + 1;

  // If we're at the last step, navigate to dragon-tiles (completion)
  if (nextIndex >= ONBOARDING_STEP_ORDER.length) {
    return "dragon-tiles";
  }

  return ONBOARDING_STEP_ORDER[nextIndex];
}

export function isStepComplete(
  step: OnboardingStepId,
  setupData: SetupResponse,
): boolean {
  switch (step) {
    case "intro":
      return !!setupData.meta.introUpdatedAt;
    case "styling":
      return !!setupData.meta.stylingUpdatedAt;
    case "operation":
      return !!setupData.meta.operationUpdatedAt;
    case "components":
      return !!setupData.meta.componentsUpdatedAt;
    case "voice":
      return !!setupData.meta.voiceUpdatedAt;
    default:
      return false;
  }
}

export function isStepAccessible(
  step: OnboardingStepId,
  setupData: SetupResponse,
  currentStep?: string,
): boolean {
  // Intro is always accessible
  if (step === "intro") return true;

  // Complete step is always disabled unless you're on it
  if (step === "complete" && currentStep === "complete") return true;
  if (step === "complete") return false;

  // Results is a hidden terminal step — only reachable while you're on it
  if (step === "results" && currentStep === "results") return true;
  if (step === "results") return false;

  // For other steps, check if the previous step is complete
  const stepIndex = ONBOARDING_STEP_ORDER.indexOf(step);
  if (stepIndex === -1) return false;
  if (stepIndex === 0) return true;

  const previousStep = ONBOARDING_STEP_ORDER[stepIndex - 1];
  return isStepComplete(previousStep, setupData);
}
